import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Sprout } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { DISTRICTS } from "@/lib/constants";
import { toast } from "sonner";
import BackButton from "@/components/BackButton";

const Register = () => {
  const { t } = useLang();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", address: "", email: "", password: "", role: "buyer", district: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.role === "seller" && !form.address.trim()) {
      setError(t("auth.addressRequired"));
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, email: form.email || null, district: form.district || null, address: form.address.trim() || null };
      const u = await register(payload);
      toast.success(t("toast.signupSuccess"));
      navigate("/welcome", { replace: true, state: { justRegistered: true } });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <BackButton />
        <div className="flex flex-col items-center mb-6">
          <div className="w-12 h-12 rounded-2xl bg-[#123A2C] flex items-center justify-center mb-3">
            <Sprout className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-extrabold text-[#14251F]">{t("auth.signupTitle")}</h1>
        </div>
        <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E5E7EB] p-6 space-y-4">
          {error && <div data-testid="signup-error" className="text-sm text-[#DC2626] bg-red-50 rounded-lg p-2.5">{error}</div>}

          {/* Role toggle */}
          <div>
            <label className="text-sm text-[#6B7280] mb-1.5 block">{t("auth.role")}</label>
            <div className="grid grid-cols-2 gap-2">
              {["buyer", "seller"].map((r) => (
                <button
                  key={r}
                  type="button"
                  data-testid={`role-${r}`}
                  onClick={() => setForm((f) => ({ ...f, role: r }))}
                  className={`py-2.5 rounded-lg text-sm font-medium border ${form.role === r ? "bg-[#123A2C] text-white border-[#123A2C]" : "bg-white text-[#6B7280] border-[#E5E7EB]"}`}
                >
                  {r === "buyer" ? t("auth.roleBuyer") : t("auth.roleSeller")}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm text-[#6B7280] mb-1 block">{t("auth.fullName")}</label>
            <input data-testid="signup-name" value={form.name} onChange={set("name")} required className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2.5 outline-none focus:border-[#123A2C]" />
          </div>
          <div>
            <label className="text-sm text-[#6B7280] mb-1 block">{t("auth.phone")}</label>
            <input data-testid="signup-phone" value={form.phone} onChange={set("phone")} required className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2.5 outline-none focus:border-[#123A2C]" />
          </div>
          <div>
            <label className="text-sm text-[#6B7280] mb-1 block">
              {t("auth.address")}{form.role === "seller" && <span className="text-[#DC2626]"> *</span>}
            </label>
            <input
              data-testid="signup-address"
              value={form.address}
              onChange={set("address")}
              placeholder={t("auth.addressPlaceholder")}
              className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2.5 outline-none focus:border-[#123A2C] placeholder:text-[#9A9E97]"
            />
          </div>
          <div>
            <label className="text-sm text-[#6B7280] mb-1 block">{t("auth.email")}</label>
            <input data-testid="signup-email" type="email" value={form.email} onChange={set("email")} className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2.5 outline-none focus:border-[#123A2C]" />
          </div>
          <div>
            <label className="text-sm text-[#6B7280] mb-1 block">{t("auth.district")}</label>
            <select data-testid="signup-district" value={form.district} onChange={set("district")} className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2.5 outline-none focus:border-[#123A2C] bg-white">
              <option value="">—</option>
              {DISTRICTS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm text-[#6B7280] mb-1 block">{t("auth.password")}</label>
            <input data-testid="signup-password" type="password" value={form.password} onChange={set("password")} required minLength={6} className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2.5 outline-none focus:border-[#123A2C]" />
          </div>

          <button data-testid="signup-submit" type="submit" disabled={loading} className="w-full py-3 rounded-xl bg-[#123A2C] text-white font-semibold hover:bg-[#0D2C21] transition-colors disabled:opacity-60">
            {t("auth.signupBtn")}
          </button>
          <p className="text-center text-sm text-[#6B7280]">
            {t("auth.haveAccount")}{" "}
            <Link data-testid="go-login" to="/login" className="text-[#123A2C] font-semibold">{t("auth.goLogin")}</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default Register;
